import { useState, useEffect } from 'react'
import Address from './Address'
import TimeSince from './TimeSince'
import { isTestTransactionHash } from '../utils/transaction'

interface Sponsorship {
  user: string
  status: string
  sponsorTxHash: string
  confirmedAt: number | null
}

interface SponsorTableProps {
  botType: 'bridge' | 'plsx'
  showAmount?: boolean
}

import { SPONSORSHIP_AMOUNT_DISPLAY } from '../constants'

export default function SponsorTable({ botType, showAmount = true }: SponsorTableProps) {
  const [sponsorships, setSponsorships] = useState<Sponsorship[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSponsorships = async () => {
      try {
        const endpoint = botType === 'bridge' ? '/api/sponsorships/bridge' : '/api/sponsorships/plsx'
        const response = await fetch(endpoint)
        if (response.ok) {
          const data = await response.json()
          setSponsorships(data)
        }
      } catch (error) {
        console.error('Error fetching sponsorships:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchSponsorships()
    // Refresh every 30 seconds
    const interval = setInterval(fetchSponsorships, 30000)
    return () => clearInterval(interval)
  }, [botType])

  if (sponsorships.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-4">
        {loading ? 'Loading...' : 'No recent sponsorships yet'}
      </p>
    )
  }

  return (
    <div className="flex flex-col my-4 space-y-2">
      <div className="shadow-md">
        <table className="w-full text-sm text-left text-gray-300">
          <thead className="bg-gray-800 text-gray-300">
            <tr>
              <th scope="col" className="px-3 py-4 leading-10">User</th>
              {showAmount && <th scope="col" className="px-3 py-4 leading-10">Sent</th>}
              <th scope="col" className="px-3 py-4 leading-10">Status</th>
              <th scope="col" className="px-3 py-4 leading-10">Tx</th>
              <th scope="col" className="px-3 py-4 leading-10">Confirmed At</th>
            </tr>
          </thead>
          <tbody>
            {sponsorships.map((row, index) => (
              <tr key={index} className="bg-gray-900 border-b border-gray-700 hover:bg-gray-800">
                <td className="px-3 py-4 leading-10 flex flex-row items-center">
                  <Address address={row.user} ellipsis={true} />
                  <a href={`https://ipfs.scan.pulsechain.com/address/${row.user}`} target="_blank" rel="noopener noreferrer">
                    <span className="ml-2">↗</span>
                  </a>
                </td>
                {showAmount && (
                  <td className="px-3 py-4 leading-10">
                    {SPONSORSHIP_AMOUNT_DISPLAY.toLocaleString()} PLS
                  </td>
                )}
                <td className="px-3 py-4 leading-10">{row.status}</td>
                <td className="px-3 py-4 leading-10">
                  <div className="flex flex-row items-center">
                    {isTestTransactionHash(row.sponsorTxHash) ? (
                      <span
                        title="Test transaction — dry-run, no real PLS was sent"
                        className="inline-flex items-center gap-1 rounded border border-amber-700 bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-300"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3h6M10 3v6.5L5.5 17a2 2 0 0 0 1.8 3h9.4a2 2 0 0 0 1.8-3L14 9.5V3" />
                        </svg>
                        test
                      </span>
                    ) : (
                      <a href={`https://ipfs.scan.pulsechain.com/tx/${row.sponsorTxHash}`} target="_blank" rel="noopener noreferrer">
                        <span className="ml-2">↗</span>
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-3 py-4 leading-10">
                  {row.confirmedAt ? <TimeSince timestamp={row.confirmedAt} /> : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

